import {useEffect} from 'react';
import {GetStaticProps} from 'next';
import Head from 'next/head';
import {dehydrate, QueryClient} from '@tanstack/react-query';
import {Flex, Grid, Heading, useBreakpointValue} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {EarnCoinsBanner} from '@eksab/components';
import {ApplyGiftCode, Package} from '@eksab/features/packages/components';
import {usePackagesQuery} from '@eksab/features/packages/hooks';
import {useAccessToken, useAppSettingsQuery} from '@eksab/hooks';
import {RegionSettings, useRegionSettings} from '@eksab/hooks/useRegionSettings';
import {setApiLocale} from '@eksab/api';
import {getLayout} from '@eksab/features/store/components/getLayout';
import {PaymentProviders} from '@eksab/features/payment/components';

export default function Coins() {
  const t = useTranslate();
  const hasUserContext = useAccessToken().fetchStatus === 'idle';
  const {data: packages, isLoading, isError} = usePackagesQuery({enabled: hasUserContext});

  const numOfShimmers = useBreakpointValue([4, null, 3])!;

  useEffect(() => {
    mixpanel?.track('Page View', {page: 'Coins'});
  }, []);

  return (
    <>
      <Head>
        <meta name="description" content="Buy coins and win | Eksab" />
        <title>{`${t('store.coins')} - ${t('app.store')} | ${t('app.name')}`}</title>
      </Head>

      <Flex
        as="header"
        flexDir={['column', null, 'row']}
        gap="4"
        align={['flex-start', null, 'center']}
        justify="space-between"
        mb="8"
      >
        <Heading as="h2" size="lg" fontWeight="700">
          {t('store.coins.buy-more-coins')}
        </Heading>

        <PaymentProviders justify={{md: 'flex-end'}} flex={1} />
      </Flex>

      <Grid
        templateColumns={[
          'repeat(2, 1fr)',
          null,
          'repeat(3, 1fr)',
          null,
          null,
          'repeat(auto-fill, minmax(380px, 1fr))',
        ]}
        gap="5"
      >
        {isError
          ? t('app.errors.try-again')
          : isLoading
            ? Array.from({length: numOfShimmers}).map((_, i) => <Package.Skeleton key={i} />)
            : packages.map((pack) => <Package key={pack.id} pack={pack} href={`/payment/coins/${pack.id}`} />)}
      </Grid>

      <ApplyGiftCode my="8" />
      <EarnCoinsBanner />
    </>
  );
}

Coins.getLayout = getLayout;

export const getStaticProps: GetStaticProps = async ({locale}) => {
  const queryClient = new QueryClient();

  setApiLocale(locale);

  const regionSettings = await queryClient.fetchQuery<RegionSettings>(
    [useRegionSettings.queryKey, locale],
    useRegionSettings.queryFn,
  );

  if (!regionSettings?.settings?.coin_packages_enabled) return {notFound: true, revalidate: 1};

  await queryClient.prefetchQuery([...useAppSettingsQuery.queryKey, locale], useAppSettingsQuery.queryFn);

  const dehydratedState = dehydrate(queryClient);
  return {
    props: {
      dehydratedState,
    },
  };
};
